import * as React from "react"
import type { Column, ColumnPinningState, RowSelectionState, VisibilityState } from "@tanstack/react-table"
import { Button, Checkbox, EmptyState, Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@nexus/ui"
import { TASK_NAVIGATION_SECTION } from "@nexus/contracts"
import { useTranslation } from "react-i18next"
import { Link } from "@tanstack/react-router"
import { Upload } from "lucide-react"
import { usePermissions, useIamPermission } from "@/features/auth/hooks/usePermissions"
import { useStopOpportunityGeneration } from "@/features/task-progress/hooks/useStopOpportunityGeneration"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import type {
  Opportunity,
  OpportunitySet,
  OpportunityTabKey,
} from "../../../types/opportunities.types"

import { computeOpportunityTabCounts } from "../../../lib/opportunity-tab-counts"
import { formatOpportunityEngineLabel } from "../../../lib/format-opportunity"
import { useOpportunityBulkAction } from "../../../hooks/useOpportunityBulkAction"
import { useExportAction } from "../../../hooks/useExportAction"
import { useOpportunitiesInfinite } from "../../../hooks/useOpportunitiesInfinite"
import { useOpportunityListSort } from "../../../hooks/useOpportunityListSort"
import type { OpportunityFacets } from "../../../api/opportunities.api"
import { OpportunitySetSelector } from "../../sets/OpportunitySetSelector/OpportunitySetSelector"
import { OpportunitySetList } from "../../sets/OpportunitySetList/OpportunitySetList"
import { OpportunitiesStatusTabs } from "../../table/OpportunitiesStatusTabs/OpportunitiesStatusTabs"
import { OpportunitiesTableToolbar, type OpportunitiesFilters } from "../../table/OpportunitiesTableToolbar/OpportunitiesTableToolbar"
import { OpportunitiesTable } from "../../table/OpportunitiesTable/OpportunitiesTable"
import { ColumnVisibilityPicker } from "../../table/ColumnVisibilityPicker/ColumnVisibilityPicker"
import { DensityToggle } from "../../table/DensityToggle/DensityToggle"
import { ViewToggle, type OpportunitiesView } from "../../table/ViewToggle/ViewToggle"
import type { TableDensity } from "../../table/OpportunitiesTable/OpportunitiesTable"
import { ExportDropdown } from "../../table/ExportDropdown/ExportDropdown"
import {
  isScriptGenerationFilterActive,
  partitionScriptGenerationSelection,
  SCRIPT_GENERATION_DATASET,
} from "../../../constants/script-generation"
import { OpportunityCardGrid } from "../../cards/OpportunityCardGrid/OpportunityCardGrid"
import { OpportunityCardSkeleton } from "../../cards/OpportunityCard/OpportunityCard"
import { BulkActionBar } from "../../table/BulkActionBar/BulkActionBar"
import {
  ScriptGenerationGateDialog,
  type ScriptGenerationGateMode,
} from "../../table/BulkActionBar/ScriptGenerationGateDialog"
import { OpportunityDrawer } from "../../drawer/OpportunityDrawer/OpportunityDrawer"
import { OpportunitiesInfoBanner } from "../OpportunitiesInfoBanner/OpportunitiesInfoBanner"

import { CreateSetDialog, type CreateSetFormValues } from "../../sets/CreateSetDialog/CreateSetDialog"
import { SetParamsPopover } from "../../sets/SetParamsPopover/SetParamsPopover"
import { RenameSetDialog } from "../../sets/RenameSetDialog/RenameSetDialog"
import { DeleteSetDialog } from "../../sets/DeleteSetDialog/DeleteSetDialog"
import { RegenSetDialog } from "../../sets/RegenSetDialog/RegenSetDialog"
import { SetGenerationStatus } from "../../sets/SetGenerationStatus/SetGenerationStatus"
import { OpportunitiesLoadingState } from "../OpportunitiesLoadingState/OpportunitiesLoadingState"
import { OpportunitiesTableFullSkeleton } from "../../table/OpportunitiesTable/OpportunitiesTableSkeletonRows"
import { OpportunitiesEmptyState } from "../OpportunitiesEmptyState/OpportunitiesEmptyState"
import { OpportunitiesErrorState } from "../OpportunitiesErrorState/OpportunitiesErrorState"
import { CompletedScriptsTab } from "../../scripts/CompletedScriptsTab/CompletedScriptsTab"
import { OpportunitySetOrdersTab } from "../../orders/OpportunitySetOrdersTab"
import { KeyboardShortcutsDialog } from "../../KeyboardShortcutsDialog/KeyboardShortcutsDialog"
import { OPPORTUNITY_DRAG_REORDER_GRID_CLASS } from "../../../lib/opportunity-drag-reorder-grid-classes"
import { OPPORTUNITY_FILTER_ALL } from "../../../constants/opportunity-filters"
import { opportunityKeys, industriesQueryOptions, adPlatformsQueryOptions, productsQueryOptions, ordersForSetQueryOptions } from "@/lib/queries"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { toTitleCase } from "@/lib/title-case"
import { useQuery } from "@tanstack/react-query"
const COLUMN_VISIBILITY_KEY = "nexus_opportunities_column_visibility"
const COLUMN_ORDER_KEY = "nexus_opportunities_column_order"
const COLUMN_PINNING_KEY = "nexus_opportunities_column_pinning"
const DENSITY_KEY = "nexus_opportunities_density"
const VIEW_KEY = "nexus_opportunities_view"
const INFO_BANNER_KEY = "nexus_opportunities_info_banner"
const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {}
const DEFAULT_COLUMN_PINNING: ColumnPinningState = { left: ["select"] }

export interface OpportunitiesPageProps {
  tenantId: string
  sets: OpportunitySet[]
  selectedSet: OpportunitySet | null
  activeTab: OpportunityTabKey
  search: string
  filters: OpportunitiesFilters
  facets?: OpportunityFacets
  /** When false, facet filter dropdowns are hidden (no OPPORTUNITIES_FACETS permission). */
  showFacetFilters?: boolean
  drawerOpportunity?: Opportunity | null
  selectedOpportunityId?: string
  /** Script id from URL — expands the matching opportunity group + script row on Completed Scripts. */
  highlightScriptId?: string
  /** Order id from URL — expands and highlights the matching order on Orders. */
  highlightOrderId?: string
  /** Task-card jump is in flight — clear Completed Scripts search/filters so the row can mount. */
  revealTaskTarget?: boolean
  onSetChange: (set: OpportunitySet | null) => void
  canListSets?: boolean
  canCreateSet?: boolean
  canRenameSet?: boolean
  canDeleteSet?: boolean
  onCreateSet?: (values: CreateSetFormValues) => void
  onRenameSet?: (id: string, name: string) => void
  onDeleteSet?: (id: string) => void
  isCreatingSet?: boolean
  generatingSetIds?: Set<string>
  /** Map of setId → unix ms when generation started. Forwarded to each set card for live elapsed display. */
  generationStartedAt?: Map<string, number>
  justCompletedSetIds?: Set<string>
  errorSetIds?: Set<string>
  /** Map of setId → backend-provided error message for FAILED sets; used to render real error copy on the card. */
  generationErrorBySetId?: Map<string, string>
  onRetrySet?: (id: string) => void
  canRegenSet?: boolean
  onRegenSet?: (id: string) => void
  isDeletingSet?: boolean
  isRenamingSet?: boolean
  onTabChange: (tab: OpportunityTabKey) => void
  onSearchChange: (q: string) => void
  onFilterChange: (key: keyof OpportunitiesFilters, value: string) => void
  onClearFilters: () => void
  onDrawerOpen: (opportunity: Opportunity) => void
  onDrawerClose: () => void
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
  /** Deep link: open Create Set modal once on mount (e.g. from Dashboard). */
  openCreateSetOnMount?: boolean
  /** Called after the mount auto-open fires so the route can clear `createSet` from the URL. */
  onCreateSetDialogOpened?: () => void
  /** Extra controls at the end of the set header, e.g. the admin "Migrate to hub" menu. */
  headerActions?: React.ReactNode
}

export function OpportunitiesPage({
  tenantId,
  sets,
  selectedSet,
  activeTab,
  search,
  filters,
  facets,
  showFacetFilters = true,
  drawerOpportunity: drawerOpportunityProp,
  selectedOpportunityId,
  highlightScriptId,
  highlightOrderId,
  revealTaskTarget = false,
  onSetChange,
  canListSets = true,
  canCreateSet = true,
  canRenameSet = true,
  canDeleteSet = true,
  onCreateSet,
  onRenameSet,
  onDeleteSet,
  isCreatingSet,
  generatingSetIds,
  generationStartedAt,
  justCompletedSetIds,
  errorSetIds,
  generationErrorBySetId,
  onRetrySet,
  canRegenSet = false,
  onRegenSet,
  isDeletingSet,
  isRenamingSet,
  onTabChange,
  onSearchChange,
  onFilterChange,
  onClearFilters,
  onDrawerOpen,
  onDrawerClose,
  isLoading,
  isError,
  onRetry,
  openCreateSetOnMount,
  onCreateSetDialogOpened,
  headerActions,
}: OpportunitiesPageProps) {
  const { t } = useTranslation()
  const perms = usePermissions()
  const canImport =
    useIamPermission(perms.MARKETPLACE_ADMIN_PARSE_ORDER_IMPORT) ||
    useIamPermission(perms.MARKETPLACE_ADMIN_VIEW_PAGE)
  const canListOrders =
    useIamPermission(perms.MARKETPLACE_VIEW_PAGE) ||
    useIamPermission(perms.MARKETPLACE_LIST_ORDERS)
  const canUseMinimalGeneration = perms.OPPORTUNITY_SETS_MINIMAL.isOn

  const { data: industries, isLoading: industriesLoading, isError: industriesError } = useQuery({
    ...industriesQueryOptions("opportunities", tenantId),
    // Needed by the create dialog AND by card meta lines (label lookup), so
    // fetch whenever the caller has any view into sets.
    enabled: (canCreateSet || canListSets) && !!tenantId,
  })
  const { data: adPlatformOptions, isLoading: adPlatformOptionsLoading } = useQuery({
    ...adPlatformsQueryOptions(tenantId),
    enabled: canCreateSet && !!tenantId,
  })
  const { data: productOptions, isLoading: productOptionsLoading } = useQuery({
    ...productsQueryOptions("opportunities", tenantId),
    enabled: canCreateSet && !!tenantId,
  })
  const industryLabelByIndex = React.useMemo(() => {
    const m = new Map<string, string>()
    for (const i of industries ?? []) m.set(i.index, i.title)
    return m
  }, [industries])

  const selectableSets = React.useMemo(
    () => sets.filter((s) => !generatingSetIds?.has(s.id)),
    [sets, generatingSetIds],
  )

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  
  const stopOpportunityGeneration = useStopOpportunityGeneration()

  const [renameTarget, setRenameTarget] = React.useState<OpportunitySet | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<OpportunitySet | null>(null)
  const [regenTarget, setRegenTarget] = React.useState<OpportunitySet | null>(null)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)
  const deletingSetIds = React.useMemo(
    () => (deletingId ? new Set([deletingId]) : undefined),
    [deletingId],
  )
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false)
  const [pendingDefaultName, setPendingDefaultName] = React.useState("")

  function openCreateDialog() {
    const now = new Date()
    const yy = String(now.getFullYear()).slice(2)
    const mm = String(now.getMonth() + 1).padStart(2, "0")
    const dd = String(now.getDate()).padStart(2, "0")
    const hh = String(now.getHours()).padStart(2, "0")
    const min = String(now.getMinutes()).padStart(2, "0")
    setPendingDefaultName(`Set ${yy}${mm}${dd}-${hh}${min}`)
    setCreateDialogOpen(true)
  }

  const openCreateDialogRef = React.useRef(openCreateDialog)
  openCreateDialogRef.current = openCreateDialog

  const didAutoOpenCreateRef = React.useRef(false)
  React.useEffect(() => {
    if (!openCreateSetOnMount || didAutoOpenCreateRef.current) return
    if (!canCreateSet || !onCreateSet || selectedSet) return
    didAutoOpenCreateRef.current = true
    openCreateDialogRef.current()
    onCreateSetDialogOpened?.()
  }, [openCreateSetOnMount, canCreateSet, onCreateSet, selectedSet, onCreateSetDialogOpened])

  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_ORDER_KEY)
      if (stored) return JSON.parse(stored) as string[]
    } catch { /* corrupted or private browsing */ }
    return []
  })

  const handleColumnOrderChange = React.useCallback((next: string[]) => {
    setColumnOrder(next)
    try { localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(next)) } catch { /* private browsing */ }
  }, [])

  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_PINNING_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnPinningState
        const left = [...(parsed.left ?? [])]
        if (!left.includes("select")) left.unshift("select")
        return { ...parsed, left }
      }
    } catch { /* corrupted or private browsing */ }
    return DEFAULT_COLUMN_PINNING
  })

  const handleColumnPinningChange = React.useCallback((next: ColumnPinningState) => {
    const left = [...(next.left ?? [])]
    if (!left.includes("select")) left.unshift("select")
    const safe = { ...next, left }
    setColumnPinning(safe)
    try { localStorage.setItem(COLUMN_PINNING_KEY, JSON.stringify(safe)) } catch { /* private browsing */ }
  }, [])

  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_VISIBILITY_KEY)
      if (stored) return JSON.parse(stored) as VisibilityState
    } catch { /* corrupted or private browsing */ }
    return DEFAULT_COLUMN_VISIBILITY
  })

  const handleColumnVisibilityChange = React.useCallback((next: VisibilityState) => {
    setColumnVisibility(next)
    try { localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(next)) } catch { /* private browsing */ }
  }, [])

  const [density, setDensity] = React.useState<TableDensity>(() => {
    try {
      const stored = localStorage.getItem(DENSITY_KEY)
      if (stored && ["condensed", "regular", "relaxed"].includes(stored)) return stored as TableDensity
    } catch { /* private browsing */ }
    return "regular"
  })
  const handleDensityChange = React.useCallback((next: TableDensity) => {
    setDensity(next)
    try { localStorage.setItem(DENSITY_KEY, next) } catch { /* private browsing */ }
  }, [])

  const [view, setView] = React.useState<OpportunitiesView>(() => {
    try {
      const stored = localStorage.getItem(VIEW_KEY)
      if (stored === "table" || stored === "card") return stored
    } catch { /* private browsing */ }
    return "table"
  })
  const handleViewChange = React.useCallback((next: OpportunitiesView) => {
    setView(next)
    try { localStorage.setItem(VIEW_KEY, next) } catch { /* private browsing */ }
  }, [])

  // Onboarding banner is collapsed by default; the lightbulb toggle reveals it.
  const [infoBannerOpen, setInfoBannerOpen] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(INFO_BANNER_KEY) === "true"
    } catch { /* private browsing */ }
    return false
  })
  const handleInfoBannerChange = React.useCallback((next: boolean) => {
    setInfoBannerOpen(next)
    try { localStorage.setItem(INFO_BANNER_KEY, String(next)) } catch { /* private browsing */ }
  }, [])

  const {
    columnSort,
    listSort,
    sortParam,
    handleColumnSortChange,
    handleManualRowReorder,
  } = useOpportunityListSort(tenantId, selectedSet?.id)
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const sentinelRef = React.useRef<HTMLDivElement>(null)

  // Escape key exits fullscreen
  React.useEffect(() => {
    if (!isFullscreen) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setIsFullscreen(false)
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [isFullscreen])

  const {
    data: infiniteData,
    totalCount: filteredTotalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isInfiniteLoading,
    isPlaceholderData,
  } = useOpportunitiesInfinite({
    setId: selectedSet?.id,
    tab: activeTab,
    filters,
    search,
    sort: sortParam,
    pageSize: 20,
    enabled: !!selectedSet && activeTab !== "orders" && activeTab !== "completed_scripts",
  })

  useInfiniteScroll(sentinelRef, scrollContainerRef, fetchNextPage, hasNextPage, isFetchingNextPage, {
    enabled: !!selectedSet && activeTab !== "orders" && activeTab !== "completed_scripts",
  })

  const drawerOpportunity = React.useMemo(() => {
    if (drawerOpportunityProp) return drawerOpportunityProp
    if (!selectedOpportunityId) return null
    return infiniteData?.find((o) => o.id === selectedOpportunityId) ?? null
  }, [drawerOpportunityProp, selectedOpportunityId, infiniteData])

  const handleFilterChange = React.useCallback((key: keyof OpportunitiesFilters, value: string) => {
    onFilterChange(key, value)
  }, [onFilterChange])

  const filterValues = React.useMemo(() => ({
    actions: facets?.actions ?? [],
    datasets: facets?.datasets ?? facets?.flowNames ?? [],
    features: facets?.features ?? [],
    subFeatures: facets?.subFeatures ?? [],
    engines: facets?.engines ?? [],
  }), [facets])

  const [tableColumns, setTableColumns] = React.useState<Column<import("../../../types/opportunities.types").Opportunity, unknown>[]>([])
  const handleColumnsReady = React.useCallback(
    (cols: Column<import("../../../types/opportunities.types").Opportunity, unknown>[]) => setTableColumns(cols),
    [],
  )

  const setId = selectedSet?.id ?? ""

  const canExport = perms.OPPORTUNITIES_EXPORT.isOn

  const scripts = useOpportunityBulkAction({ tenantId, setId, action: "generate-scripts" })
  const reject = useOpportunityBulkAction({ tenantId, setId, action: "reject" })

  const isAuthenticated = useAuth((s) => s.isAuthenticated)

  const {
    data: setOrdersPage,
    isLoading: isTenantOrdersLoading,
    isError: isTenantOrdersError,
  } = useQuery({
    ...ordersForSetQueryOptions(tenantId, setId),
    enabled: !!selectedSet && !!setId && canListOrders && isAuthenticated,
  })
  const setOrders = setOrdersPage?.orders ?? []

  const tabCounts = React.useMemo(
    () => computeOpportunityTabCounts(selectedSet, setOrdersPage?.total ?? setOrders.length),
    [selectedSet, setOrders.length, setOrdersPage?.total],
  )
  const exportAction = useExportAction()

  const selectedIds = React.useMemo(
    () => Object.keys(rowSelection).filter((k) => rowSelection[k]),
    [rowSelection],
  )

  const [scriptsGateOpen, setScriptsGateOpen] = React.useState(false)
  const [scriptsGateMode, setScriptsGateMode] = React.useState<ScriptGenerationGateMode>("all_ineligible")

  const scriptSelection = React.useMemo(
    () => partitionScriptGenerationSelection(infiniteData ?? [], selectedIds),
    [infiniteData, selectedIds],
  )

  const runScriptGeneration = React.useCallback(
    (opportunityIds: string[]) => {
      if (opportunityIds.length === 0) return
      scripts.execute({ opportunityIds })
      onTabChange("completed_scripts")
    },
    [scripts, onTabChange],
  )

  const handleGenerateScripts = React.useCallback(() => {
    if (selectedIds.length === 0) return

    if (isScriptGenerationFilterActive(filters.dataset)) {
      runScriptGeneration(selectedIds)
      return
    }

    const { eligibleIds, ineligibleIds, unknownIds } = scriptSelection

    if (unknownIds.length > 0) {
      setScriptsGateMode("unknown")
      setScriptsGateOpen(true)
      return
    }

    if (ineligibleIds.length === 0) {
      runScriptGeneration(eligibleIds.length > 0 ? eligibleIds : selectedIds)
      return
    }

    if (eligibleIds.length === 0) {
      setScriptsGateMode("all_ineligible")
      setScriptsGateOpen(true)
      return
    }

    setScriptsGateMode("partial")
    setScriptsGateOpen(true)
  }, [selectedIds, filters.dataset, scriptSelection, runScriptGeneration])

  const handleSwitchToTopicsDataset = React.useCallback(() => {
    setScriptsGateOpen(false)
    setRowSelection({})
    onFilterChange("dataset", SCRIPT_GENERATION_DATASET)
  }, [onFilterChange])

  const handleGenerateEligibleScripts = React.useCallback(() => {
    setScriptsGateOpen(false)
    const { eligibleIds, ineligibleIds } = scriptSelection
    setRowSelection((prev) => {
      const next = { ...prev }
      for (const id of ineligibleIds) delete next[id]
      return next
    })
    runScriptGeneration(eligibleIds)
  }, [scriptSelection, runScriptGeneration])

  const jobItems = React.useMemo(() => {
    const items: Record<string, string> = {}
    if (scripts.state.type === "polling" || scripts.state.type === "done") Object.assign(items, scripts.state.items)
    if (reject.state.type === "polling" || reject.state.type === "done") Object.assign(items, reject.state.items)
    return Object.keys(items).length > 0 ? items : undefined
  }, [scripts.state, reject.state])


  React.useEffect(() => {
    setRowSelection({})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSet?.id, activeTab, search, filters.action, filters.dataset, filters.feature, filters.subFeature, filters.engine, listSort])

  React.useEffect(() => {
    if (reject.state.type === "done") setRowSelection({})
  }, [reject.state.type])

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && Object.keys(rowSelection).length > 0) {
        setRowSelection({})
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [rowSelection])

  const opportunityShortcuts = React.useMemo(() => {
    const base = [
      { keys: ["/"], label: t("opportunities.table.shortcuts.search") },
      { keys: ["Esc"], label: t("opportunities.table.shortcuts.clearSelection") },
      { keys: ["?"], label: t("opportunities.table.shortcuts.help") },
    ]
    if (view !== "table") return base
    return [
      { keys: ["j", "↓"], label: t("opportunities.table.shortcuts.navigateDown") },
      { keys: ["k", "↑"], label: t("opportunities.table.shortcuts.navigateUp") },
      { keys: ["Enter"], label: t("opportunities.table.shortcuts.open") },
      { keys: ["Space"], label: t("opportunities.table.shortcuts.select") },
      ...base,
    ]
  }, [t, view])

  React.useEffect(() => {
    if (activeTab === "completed_scripts" || activeTab === "orders") return

    function isEditableTarget() {
      const ae = document.activeElement
      if (!ae) return false
      const tag = ae.tagName
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (ae as HTMLElement).isContentEditable
      )
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "?" || isEditableTarget() || e.metaKey || e.ctrlKey || e.altKey) return
      e.preventDefault()
      setShortcutsOpen((prev) => !prev)
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [activeTab])

  if (isLoading) {
    return <OpportunitiesLoadingState variant={selectedSet ? "table" : "sets"} />
  }
  if (isError) return <OpportunitiesErrorState onRetry={onRetry} />

  const selectedCount = Object.keys(rowSelection).length
  const bulkChromeVisible =
    selectedCount > 0 ||
    scripts.state.type !== "idle" ||
    reject.state.type !== "idle"

  return (
    <div className={cn(
      "flex min-h-0 flex-1 flex-col transition-all duration-300 ease-in-out",
      isFullscreen && "fixed inset-0 z-50 bg-background ring-2 ring-inset ring-primary/25",
    )}>
      <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4">

        {/* Tier 1 — Set context (hidden when there are no selectable sets; empty state handles create CTA) */}
        {canListSets && !isFullscreen && (selectedSet || selectableSets.length > 0) && (
          <div className="flex shrink-0 items-center gap-2">
            {selectedSet && (
              <button
                type="button"
                onClick={() => onSetChange(null)}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Icon name="arrow-left" size="xs" />
                {t("opportunities.allSets")}
              </button>
            )}
            {selectedSet && (
              <Icon name="chevron-right" size="xs" className="text-muted-foreground/40" />
            )}
            <OpportunitySetSelector
              sets={sets}
              selectedSet={selectedSet}
              onSelect={onSetChange}
              {...(selectedSet ? { onSeeAll: () => onSetChange(null) } : {})}
              onCreateSet={onCreateSet ? openCreateDialog : undefined}
              canCreateSet={canCreateSet && !(generatingSetIds && generatingSetIds.size > 0)}
              generatingSetIds={generatingSetIds}
            />
            {selectedSet && (
              <SetParamsPopover
                set={selectedSet}
                industryLabel={industryLabelByIndex.get(selectedSet.industry ?? "") ?? null}
                benchmarkIndustryLabel={
                  selectedSet.benchmarkIndustry
                    ? (industryLabelByIndex.get(selectedSet.benchmarkIndustry) ?? selectedSet.benchmarkIndustry)
                    : null
                }
                isGenerating={!!generatingSetIds?.has(selectedSet.id)}
                alwaysVisible
              />
            )}
            {!selectedSet && canCreateSet && !!onCreateSet && (() => {
              const isBusy = !!(isCreatingSet || (generatingSetIds && generatingSetIds.size > 0))
              return (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  {...(isBusy ? {} : { iconStart: "plus" as const })}
                  disabled={isBusy}
                  onClick={openCreateDialog}
                >
                  {isBusy && <Icon name="loader" size="xs" color="inherit" className="mr-1.5 animate-spin" />}
                  {isCreatingSet
                    ? t("common.creating")
                    : generatingSetIds && generatingSetIds.size > 0
                      ? t("opportunities.generating")
                      : t("opportunities.newSet")}
                </Button>
              )
            })()}
            <div className="ml-auto flex items-center gap-2">
              {canImport ? (
                <Button variant="outline" size="sm" className="shrink-0" asChild>
                  <Link to="/marketplace/import">
                    <Upload className="mr-2 h-4 w-4" />
                    {t("marketplace.admin.import.navAction")}
                  </Link>
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => handleInfoBannerChange(!infoBannerOpen)}
                aria-expanded={infoBannerOpen}
                aria-label={infoBannerOpen ? t("opportunities.infoBanner.collapseAria") : t("opportunities.infoBanner.triggerAria")}
                className={cn(
                  "min-h-11 min-w-11 text-muted-foreground hover:text-foreground [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7",
                  infoBannerOpen && "bg-primary/10 text-primary hover:text-primary",
                )}
              >
                <Icon name="lightbulb" size="sm" color="inherit" />
              </Button>
              {selectedSet?.status === "READY" && selectedSet.generationCompletedAt && (
                <TooltipProvider delayDuration={400}>
                  <SetGenerationStatus
                    generatedAt={selectedSet.generationCompletedAt}
                    canRegen={canRegenSet && onRegenSet != null}
                    isRegenerating={generatingSetIds?.has(selectedSet.id)}
                    onRegen={canRegenSet && onRegenSet ? () => setRegenTarget(selectedSet) : undefined}
                  />
                </TooltipProvider>
              )}
              {headerActions}
            </div>
          </div>
        )}

        {canListSets && !isFullscreen && !selectedSet && selectableSets.length === 0 && (canImport || headerActions) ? (
          <div className="flex shrink-0 items-center justify-end gap-2">
            {canImport ? (
              <Button variant="outline" size="sm" className="shrink-0" asChild>
                <Link to="/marketplace/import">
                  <Upload className="mr-2 h-4 w-4" />
                  {t("marketplace.admin.import.navAction")}
                </Link>
              </Button>
            ) : null}
            {headerActions}
          </div>
        ) : null}

        {!isFullscreen && (
          <OpportunitiesInfoBanner
            open={infoBannerOpen}
            onClose={() => handleInfoBannerChange(false)}
          />
        )}

        {selectedSet ? (
          <div key="table-view" className="animate-in fade-in duration-200 flex min-h-0 min-w-0 flex-1 flex-col gap-3">

            {/* Tier 2 — Tab navigation */}
            <OpportunitiesStatusTabs
              counts={tabCounts}
              activeTab={activeTab}
              onTabChange={onTabChange}
              showOrdersTab={canListOrders}
            />

            {/* Tier 3 — Search + filters (hidden on scripts and orders tabs) */}
            {activeTab !== "completed_scripts" && activeTab !== "orders" && <OpportunitiesTableToolbar
              search={search}
              onSearchChange={onSearchChange}
              filters={filters}
              onFilterChange={handleFilterChange}
              showFacetFilters={showFacetFilters}
              onClearAll={() => {
                onSearchChange("")
                onClearFilters()
              }}
              actionOptions={[
                { value: "all", label: t("opportunities.filters.allActions") },
                ...filterValues.actions.map((a) => ({ value: a, label: a.charAt(0).toUpperCase() + a.slice(1) })),
              ]}
              datasetOptions={[
                { value: "all", label: t("opportunities.filters.allFlowNames") },
                ...filterValues.datasets.map((n) => ({ value: n, label: n })),
                ...(filters.dataset !== OPPORTUNITY_FILTER_ALL && filters.dataset.trim() !== "" && !filterValues.datasets.includes(filters.dataset)
                  ? [{ value: filters.dataset, label: filters.dataset }]
                  : []),
              ]}
              featureOptions={[
                { value: "all", label: t("opportunities.filters.allFeatures") },
                ...filterValues.features.map((f) => ({ value: f, label: f })),
                ...(filters.feature !== OPPORTUNITY_FILTER_ALL && filters.feature.trim() !== "" && !filterValues.features.includes(filters.feature)
                  ? [{ value: filters.feature, label: filters.feature }]
                  : []),
              ]}
              subFeatureOptions={[
                { value: "all", label: t("opportunities.filters.allSubFeatures") },
                ...filterValues.subFeatures.map((s) => ({ value: s, label: s })),
                ...(filters.subFeature !== OPPORTUNITY_FILTER_ALL && filters.subFeature.trim() !== "" && !filterValues.subFeatures.includes(filters.subFeature)
                  ? [{ value: filters.subFeature, label: filters.subFeature }]
                  : []),
              ]}
              engineOptions={[
                { value: "all", label: t("opportunities.filters.allEngines") },
                ...filterValues.engines.map((e) => ({ value: e, label: formatOpportunityEngineLabel(e, t) })),
              ]}
            >
              <TooltipProvider delayDuration={400}>
                {isFullscreen && (
                  <span
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-2.5 text-xs font-medium text-primary"
                    role="status"
                  >
                    <Icon name="maximize-2" size="xs" color="inherit" />
                    {t("opportunities.table.fullscreenActive")}
                  </span>
                )}
                {isFullscreen && selectedSet?.status === "READY" && selectedSet.generationCompletedAt && (
                  <SetGenerationStatus
                    generatedAt={selectedSet.generationCompletedAt}
                    canRegen={canRegenSet && onRegenSet != null}
                    isRegenerating={generatingSetIds?.has(selectedSet.id)}
                    onRegen={canRegenSet && onRegenSet ? () => setRegenTarget(selectedSet) : undefined}
                  />
                )}
                {view === "table" && <DensityToggle value={density} onChange={handleDensityChange} />}
                {view === "table" && (
                  <ColumnVisibilityPicker
                    columns={tableColumns}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                    columnOrder={columnOrder}
                    onColumnOrderChange={handleColumnOrderChange}
                    columnPinning={columnPinning}
                    onColumnPinningChange={handleColumnPinningChange}
                    onReset={() => {
                      handleColumnVisibilityChange(DEFAULT_COLUMN_VISIBILITY)
                      handleColumnOrderChange([])
                      handleColumnPinningChange(DEFAULT_COLUMN_PINNING)
                    }}
                  />
                )}
                {canExport && selectedSet && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <ExportDropdown
                          tenantId={tenantId}
                          setId={selectedSet.id}
                          selectedIds={selectedIds}
                          selectedCount={selectedCount}
                          tab={activeTab}
                          search={search}
                          filters={filters}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>{t("opportunities.table.exportTooltip")}</TooltipContent>
                  </Tooltip>
                )}
                <ViewToggle value={view} onChange={handleViewChange} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      iconStart={isFullscreen ? "minimize-2" : "maximize-2"}
                      onClick={() => setIsFullscreen((v) => !v)}
                      aria-pressed={isFullscreen}
                      aria-label={isFullscreen ? t("opportunities.table.exitFullscreen") : t("opportunities.table.fullscreen")}
                      className={cn(
                        isFullscreen &&
                          "border-primary bg-primary/10 text-primary shadow-sm ring-2 ring-primary/20 dark:bg-primary/15",
                      )}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{isFullscreen ? t("opportunities.table.exitFullscreen") : t("opportunities.table.fullscreen")}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </OpportunitiesTableToolbar>}

            {/* Row count / select-all (card) + keyboard shortcut hint */}
            {activeTab !== "completed_scripts" && activeTab !== "orders" && !isInfiniteLoading && infiniteData && infiniteData.length > 0 && (() => {
              const loadedCount = infiniteData.length
              const tabTotalCount = tabCounts[activeTab]
              // `filteredTotalCount` reflects the API result for the current filters (incl. the
              // default Topics dataset filter). `tabTotalCount` is the unfiltered badge number.
              // When they differ, the table is narrowed and the message must show both numbers
              // — otherwise "Showing 6 of 33" reads as a bug when 27 rows were filtered out.
              const filteredTotal = filteredTotalCount
              const isNarrowed = filteredTotal < tabTotalCount
              const totalCount = isNarrowed ? filteredTotal : tabTotalCount

              const shortcutsHint = (
                <button
                  type="button"
                  onClick={() => setShortcutsOpen(true)}
                  className="flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={t("opportunities.table.shortcuts.title")}
                >
                  <span>{t("opportunities.table.shortcuts.footerHint")}</span>
                  <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    ?
                  </kbd>
                </button>
              )

              if (view === "card") {
                const allCardSelected = infiniteData.every((o) => rowSelection[o.id])
                const someCardSelected = infiniteData.some((o) => rowSelection[o.id])

                function toggleSelectAllCard(checked: boolean) {
                  if (checked) {
                    setRowSelection(Object.fromEntries(infiniteData.map((o) => [o.id, true])))
                  } else {
                    setRowSelection({})
                  }
                }

                return (
                  <div className="flex shrink-0 items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="select-all-opportunities"
                        checked={someCardSelected && !allCardSelected ? "indeterminate" : allCardSelected}
                        onCheckedChange={(v) => toggleSelectAllCard(v === true)}
                        className="shrink-0"
                      />
                      <label htmlFor="select-all-opportunities" className="cursor-pointer text-xs text-muted-foreground">
                        {t("opportunities.table.selectAllCount", { count: loadedCount })}
                      </label>
                      {(hasNextPage || loadedCount < totalCount || isNarrowed) && (
                        <span className="text-[11px] tabular-nums text-muted-foreground/70">
                          {isNarrowed
                            ? t("opportunities.table.loadedOfTotalFiltered", {
                                loaded: loadedCount,
                                total: filteredTotal,
                                of: tabTotalCount,
                              })
                            : t("opportunities.table.loadedOfTotal", {
                                loaded: loadedCount,
                                total: totalCount,
                              })}
                        </span>
                      )}
                    </div>
                    {shortcutsHint}
                  </div>
                )
              }

              return (
                <div className="flex shrink-0 items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>
                      {isNarrowed
                        ? t("opportunities.table.showingFiltered", {
                            loaded: loadedCount,
                            total: filteredTotal,
                            of: tabTotalCount,
                          })
                        : t("opportunities.table.showing", { loaded: loadedCount, total: totalCount })
                      }
                    </span>
                    {hasNextPage && (
                      <>
                        <span className="text-muted-foreground/60">·</span>
                        <span className="text-muted-foreground/60">{t("opportunities.table.scrollForMore")}</span>
                      </>
                    )}
                  </div>
                  {shortcutsHint}
                </div>
              )
            })()}

            {/* Content area — table, card grid, or completed-scripts list — each scrolls independently.
                Completed Scripts composes its own cards (DiversificationImpact + opportunity groups),
                so we skip the outer card frame on that tab to avoid nested-card chrome. */}
            <div
              ref={scrollContainerRef}
              data-task-section={
                activeTab === "completed_scripts"
                  ? TASK_NAVIGATION_SECTION.OPPORTUNITY_SCRIPTS
                  : undefined
              }
              className={cn(
              "min-h-0 min-w-0 flex-1 scroll-mt-6",
              activeTab === "completed_scripts" || activeTab === "orders"
                ? "overflow-hidden flex flex-col"
                : view === "card"
                ? "overflow-y-auto overflow-x-visible"
                : "overflow-auto rounded-md border border-border bg-card shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]",
            )}>
              {activeTab === "orders" ? (
                <OpportunitySetOrdersTab
                  opportunitySetId={setId}
                  tenantId={tenantId}
                  orders={setOrders}
                  highlightOrderId={highlightOrderId}
                  isLoading={isTenantOrdersLoading}
                  isError={isTenantOrdersError}
                />
              ) : activeTab === "completed_scripts" ? (
                <CompletedScriptsTab
                  setId={setId}
                  highlightScriptId={highlightScriptId}
                  revealTaskTarget={revealTaskTarget || Boolean(highlightScriptId)}
                  isGenerating={scripts.state.type === "pending" || scripts.state.type === "polling"}
                  adPlatforms={selectedSet.adPlatforms}
                />
              ) : view === "card" ? (
                isInfiniteLoading && !isPlaceholderData ? (
                  <div className={OPPORTUNITY_DRAG_REORDER_GRID_CLASS}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className="motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:fill-mode-both motion-safe:duration-300 motion-reduce:animate-none"
                        style={{ animationDelay: `${i * 50}ms` }}
                      >
                        <OpportunityCardSkeleton />
                      </div>
                    ))}
                  </div>
                ) : (
                  <OpportunityCardGrid
                    tenantId={tenantId}
                    setId={selectedSet.id}
                    data={infiniteData}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    onRowClick={onDrawerOpen}
                    hasActiveFilters={search !== "" || Object.values(filters).some((v) => v !== "all")}
                    onClearFilters={() => {
                      onSearchChange("")
                      onClearFilters()
                    }}
                    isFetchingNextPage={isFetchingNextPage}
                    search={search}
                    onManualRowReorder={handleManualRowReorder}
                    columnSortActive={columnSort.length > 0}
                  />
                )
              ) : (
                isInfiniteLoading && !isPlaceholderData ? (
                  <OpportunitiesTableFullSkeleton />
                ) : (
                  <OpportunitiesTable
                    tenantId={tenantId}
                    setId={selectedSet.id}
                    density={density}
                    data={infiniteData}
                    tabKey={activeTab}
                    hasActiveFilters={search !== "" || Object.values(filters).some((v) => v !== "all")}
                    onClearFilters={() => {
                      onSearchChange("")
                      onClearFilters()
                    }}
                    search={search}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    onRowClick={onDrawerOpen}
                    jobItems={jobItems as Record<string, import("@nexus/contracts").RowJobStatus> | undefined}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                    columnOrder={columnOrder}
                    onColumnOrderChange={handleColumnOrderChange}
                    columnPinning={columnPinning}
                    onColumnPinningChange={handleColumnPinningChange}
                    onColumnsReady={handleColumnsReady}
                    sorting={columnSort}
                    onSortingChange={handleColumnSortChange}
                    onManualRowReorder={handleManualRowReorder}
                    isFetchingNextPage={isFetchingNextPage}
                    industryLabelByIndex={industryLabelByIndex}
                    enableKeyboardShortcuts={view === "table"}
                  />
                )
              )}
              {activeTab !== "completed_scripts" && activeTab !== "orders" ? (
                <div ref={sentinelRef} className="min-h-px shrink-0" aria-hidden />
              ) : null}
            </div>

            <BulkActionBar
              selectedCount={selectedCount}
              activeTab={activeTab}
              scriptsState={scripts.state}
              rejectState={reject.state}
              onClearSelection={() => setRowSelection({})}
              onReject={(reason) => reject.execute({ opportunityIds: selectedIds, reason })}
              onGenerateScripts={handleGenerateScripts}
              onDownloadCsv={
                canExport && selectedSet
                  ? () => exportAction.execute({
                      mode: "selected",
                      tenantId,
                      setId: selectedSet.id,
                      opportunityIds: selectedIds,
                    })
                  : undefined
              }
              isDownloadingCsv={exportAction.isExporting}
            />

            <ScriptGenerationGateDialog
              open={scriptsGateOpen}
              onOpenChange={setScriptsGateOpen}
              mode={scriptsGateMode}
              selectedCount={selectedIds.length}
              eligibleCount={scriptSelection.eligibleIds.length}
              ineligibleCount={scriptSelection.ineligibleIds.length}
              unknownCount={scriptSelection.unknownIds.length}
              ineligibleDatasets={scriptSelection.ineligibleDatasets}
              onSwitchToTopics={handleSwitchToTopicsDataset}
              onGenerateEligible={handleGenerateEligibleScripts}
            />

            <OpportunityDrawer
              opportunity={drawerOpportunity}
              open={!!drawerOpportunity}
              onOpenChange={(open) => { if (!open) onDrawerClose() }}
            />

            {activeTab !== "completed_scripts" && activeTab !== "orders" && (
              <KeyboardShortcutsDialog
                open={shortcutsOpen}
                onOpenChange={setShortcutsOpen}
                title={t("opportunities.table.shortcuts.title")}
                shortcuts={opportunityShortcuts}
              />
            )}
          </div>
        ) : (
          <div key="grid-view" data-task-section={TASK_NAVIGATION_SECTION.OPPORTUNITY_SETS} className="animate-in fade-in duration-200 min-h-0 min-w-0 flex-1 scroll-mt-6 overflow-y-auto overflow-x-visible">
            {!canListSets ? (
              <EmptyState
                icon="lock"
                title={t("opportunities.noListPermissionTitle")}
                description={t("opportunities.noListPermissionDescription")}
              />
            ) : sets.length === 0 ? (
              <OpportunitiesEmptyState
                canCreateSet={canCreateSet}
                isCreatingSet={isCreatingSet}
                isGenerating={!!(generatingSetIds && generatingSetIds.size > 0)}
                onCreateSet={canCreateSet && onCreateSet ? openCreateDialog : undefined}
              />
            ) : (
              <OpportunitySetList
                sets={sets}
                onSelectSet={onSetChange}
                onRenameSet={(set) => setRenameTarget(set)}
                canRenameSet={canRenameSet}
                onDeleteSet={(set) => setDeleteTarget(set)}
                canDeleteSet={canDeleteSet}
                generatingSetIds={generatingSetIds}
                generationStartedAt={generationStartedAt}
                justCompletedSetIds={justCompletedSetIds}
                errorSetIds={errorSetIds}
                deletingSetIds={deletingSetIds}
                generationErrorBySetId={generationErrorBySetId}
                industryLabelByIndex={industryLabelByIndex}
                onRetrySet={onRetrySet ? (set) => onRetrySet(set.id) : undefined}
                onRefreshSet={canRegenSet && onRegenSet ? (set) => setRegenTarget(set) : undefined}
                canRefreshSet={canRegenSet}
              />
            )}
          </div>
        )}

        <CreateSetDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          initialName={pendingDefaultName}
          isSubmitting={isCreatingSet}
          industries={industriesError ? undefined : industries}
          industriesLoading={industriesLoading}
          adPlatformOptions={adPlatformOptions}
          adPlatformOptionsLoading={adPlatformOptionsLoading}
          productOptions={productOptions?.map((p) => ({ id: p.name, value: p.name, label: toTitleCase(p.name) }))}
          productOptionsLoading={productOptionsLoading}
          canUseMinimalGeneration={canUseMinimalGeneration}
          onSubmit={(values) => {
            setCreateDialogOpen(false)
            onCreateSet?.(values)
          }}
        />

      {renameTarget && (
        <RenameSetDialog
          open={!!renameTarget}
          onOpenChange={(open) => { if (!open) setRenameTarget(null) }}
          currentName={renameTarget.name}
          existingNames={sets.filter((s) => s.id !== renameTarget.id).map((s) => s.name)}
          onSubmit={(name) => {
            onRenameSet?.(renameTarget.id, name)
            setRenameTarget(null)
          }}
          isSubmitting={isRenamingSet}
        />
      )}

      {deleteTarget && (
        <DeleteSetDialog
          open={!!deleteTarget}
          onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}
          setName={deleteTarget.name}
          isGenerating={generatingSetIds?.has(deleteTarget.id)}
          onConfirm={() => {
            const id = deleteTarget.id
            const isGenerating = generatingSetIds?.has(id) ?? false
            setDeleteTarget(null)
            setDeletingId(id)
            // For a generating set, run the real cancellation (same steps as the task
            // sidebar's Stop): signal the worker to abort + clear the task card. The
            // worker soft-deletes the set; the optimistic remove below hides it now.
            // NOTE: stopOpportunityGeneration returns false when the task-progress record
            // is absent (initTask failed silently or Redis TTL expired). In that case the
            // set is still soft-deleted below but the worker keeps running until it finishes
            // and self-releases the lock via safeUpdateSetStatus. Full fix (cancel-by-setId
            // without a task record) requires a schema or API change — tracked as follow-up.
            if (isGenerating) stopOpportunityGeneration(id)
            setTimeout(() => { onDeleteSet?.(id) }, 220)
          }}
          isDeleting={isDeletingSet}
        />
      )}

      {regenTarget && (
        <RegenSetDialog
          open={!!regenTarget}
          onOpenChange={(open) => { if (!open) setRegenTarget(null) }}
          setName={regenTarget.name}
          onConfirm={() => {
            const id = regenTarget.id
            setRegenTarget(null)
            onRegenSet?.(id)
          }}
        />
      )}
      </div>
    </div>
  )
}
